
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, MessageCircle, Calendar, X, Video } from "lucide-react";
import { useLeadSubmission } from "@/hooks/useLeadSubmission";
import { useFormNotifications } from "@/hooks/useFormNotifications";
import DateTimePicker from "./DateTimePicker";

const StickyMinimizedForm = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [mobile, setMobile] = useState("");
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState("");
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const { submitLead, isSubmitting } = useLeadSubmission();
  const { notifyPhoneSubmission, notifyCallbackScheduled } = useFormNotifications();

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector('section');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        const isHeroVisible = rect.bottom > 100;
        setIsVisible(!isHeroVisible);

        // Auto-collapse when hero comes back into view
        if (isHeroVisible && isExpanded) {
          setIsExpanded(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true
    });

    // Listen for widget state changes to keep state in sync
    const handleWidgetStateChange = (event: CustomEvent) => {
      setIsChatWidgetOpen(event.detail.isOpen);
    };
    
    window.addEventListener('whatsAppWidgetStateChange', handleWidgetStateChange as EventListener);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('whatsAppWidgetStateChange', handleWidgetStateChange as EventListener);
    };
  }, [isExpanded]);

  const showPhoneRequiredError = () => {
    setShowPhoneError(true);
    setTimeout(() => setShowPhoneError(false), 3000);
  };

  const scrollToMainForm = () => {
    const heroSection = document.querySelector('section');
    if (heroSection) {
      heroSection.scrollIntoView({ behavior: 'smooth' });
      
      // Show red bubble for 3 seconds
      const phoneInput = document.querySelector('input[type="tel"]') as HTMLInputElement;
      if (phoneInput) {
        phoneInput.focus();
        // Trigger the phone required error on main form
        window.dispatchEvent(new CustomEvent('showPhoneError'));
      }
    }
  };

  const triggerMainFormAction = (action: 'callback' | 'schedule') => {
    if (action === 'callback') {
      // Trigger immediate callback from main form
      window.dispatchEvent(new CustomEvent('triggerImmediateCallback'));
    } else {
      // Trigger schedule callback from main form
      window.dispatchEvent(new CustomEvent('triggerScheduleCallback'));
    }
  };

  const handleCallback = async () => {
    if (!mobile.trim()) {
      scrollToMainForm();
      return;
    }
    
    // If phone exists, trigger main form action
    triggerMainFormAction('callback');
  };

  const handleSchedule = () => {
    if (!mobile.trim()) {
      scrollToMainForm();
      return;
    }
    
    // If phone exists, trigger main form action
    triggerMainFormAction('schedule');
  };

  const handleCall = () => {
    // Fire GTM event for call click
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_call_click' });
    }
    
    window.location.href = 'tel:+4915754767196';
  };

  const handleChat = () => {
    // Fire GTM event for chat open click
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_chat_open_click' });
    }
    
    // Toggle the WhatsApp widget
    if (isChatWidgetOpen) {
      window.dispatchEvent(new CustomEvent('hideWhatsAppWidget'));
    } else {
      window.dispatchEvent(new CustomEvent('showWhatsAppWidget'));
    }
  };

  const handleGetCallbackClick = () => {
    // Fire GTM event for callback request click
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_callback_request_click' });
    }
    
    if (!mobile.trim()) {
      scrollToMainForm();
      return;
    }
    
    // If phone exists, trigger main form action
    triggerMainFormAction('callback');
  };

  const handleScheduleConfirm = async (dateTime: string) => {
    setSelectedDateTime(dateTime);
    
    // Send to Telegram
    await notifyCallbackScheduled({
      name: 'Visitor',
      phone: mobile,
      scheduledDate: dateTime,
      notes: 'Scheduled from mini widget'
    });
    
    const success = await submitLead(`${mobile}|${dateTime}`, 'sticky_scheduled');
    if (success) {
      setIsExpanded(false);
      setShowDateTimePicker(false);

      // Open WhatsApp to schedule a meeting message
      const waText = `Hi, I want to schedule a meeting for picking clothes on camera. ${dateTime}`;
      window.open('https://wa.me/4915754767196?text=' + encodeURIComponent(waText), '_blank');
    }
  };

  const handleZoomMeeting = () => {
    // Fire GTM event for zoom click
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_zoom_click' });
    }
    
    // Also open WhatsApp to ask for Zoom link
    window.open('https://wa.me/4915754767196?text=' + encodeURIComponent('Hi, can you share the Zoom meeting link?'), '_blank');
  };

  const handleSarahImageClick = () => {
    // Fire GTM event for chat open click
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_chat_open_click' });
    }
    
    // Toggle the WhatsApp widget
    if (isChatWidgetOpen) {
      window.dispatchEvent(new CustomEvent('hideWhatsAppWidget'));
    } else {
      window.dispatchEvent(new CustomEvent('showWhatsAppWidget'));
    }
  };

  // Desktop-only smart dock: adjust bottom offset above footer (overlap-aware)
  const dockRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isVisible) return; // ensure widget is mounted and visible
    const mm = window.matchMedia('(min-width: 768px)'); // align with md: breakpoint
    const el = dockRef.current;
    if (!el) return;
    const BASE_BOTTOM = 16; // px, Tailwind bottom-4
    el.style.transition = 'bottom 120ms linear';
    let rafId = 0;

    const applyBottom = (valuePx: number) => {
      const next = Math.max(BASE_BOTTOM, Math.round(valuePx));
      el.style.bottom = `${next}px`;
    };

    const updateOffset = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const footer = document.querySelector('footer') as HTMLElement | null;
        // If not desktop or no footer, reset to base
        if (!mm.matches || !footer) {
          applyBottom(BASE_BOTTOM);
          return;
        }
        const footerRect = footer.getBoundingClientRect();
        const widgetRect = el.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight;
        const footerVisible = footerRect.top < viewportH && footerRect.bottom > 0;
        // compute desired bottom so widget's bottom sits GAP above footer top
        const GAP = 12;
        if (footerVisible) {
          const desiredBottom = Math.max(BASE_BOTTOM, (viewportH - footerRect.top) + GAP);
          applyBottom(desiredBottom);
        } else {
          applyBottom(BASE_BOTTOM);
        }
      });
    };

    // Respond to scroll/resize and media changes (simple + reliable)
    window.addEventListener('scroll', updateOffset, { passive: true });
    window.addEventListener('resize', updateOffset);
    if (typeof (mm as any).addEventListener === 'function') {
      (mm as any).addEventListener('change', updateOffset);
    }
    // Initial position
    applyBottom(BASE_BOTTOM);
    updateOffset();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updateOffset as EventListener);
      window.removeEventListener('resize', updateOffset as EventListener);
      if (typeof (mm as any).removeEventListener === 'function') {
        (mm as any).removeEventListener('change', updateOffset);
      }
      applyBottom(BASE_BOTTOM);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <div ref={dockRef} className="fixed bottom-4 left-4 right-4 md:right-auto md:left-4 z-50">
        {!isExpanded ? (
          /* Minimized widget - responsive layout */
          <div className="bg-[#364264] rounded-lg p-4 shadow-xl border border-[#747DA1]/30 animate-slide-up w-full md:w-[570px] md:mr-auto relative">
            {/* Phone Required Error Bubble for Mini Widget */}
            {showPhoneError && (
              <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap z-50 animate-fade-in">
                <div className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-red-500"></div>
                Please click "Request a callback" to enter your phone number first
              </div>
            )}
            
            {/* Mobile layout - profile and 2x2 button grid */}
            <div className="md:hidden space-y-4">
              {/* Profile section */}
              <div className="flex items-center gap-3">
                <div 
                  className="relative cursor-pointer" 
                  onClick={handleSarahImageClick}
                >
                  <div className="relative">
                    {/* Ripple animation behind avatar */}
                    <div className="absolute inset-0 rounded-full animate-ping bg-green-500/30 scale-110"></div>
                    <img 
                      src="/images/sara-headshot.png" 
                      alt="Live Support" 
                      width={56} height={56}
                      className="relative w-14 h-14 rounded-full border-4 border-green-500 object-cover object-top z-10 shadow-lg shadow-green-500/20" 
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white z-20"></div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-[#F4F4F4] text-sm font-semibold mb-1">Rebecca is Online</p>
                  <p className="text-[#CCCCCC] text-xs">Need help? Click to get started!</p>
                </div>
              </div>
              
              {/* Buttons - 2x2 grid on mobile */}
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={handleGetCallbackClick}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-xs font-semibold shadow-lg flex items-center justify-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Request a callback
                </Button>
                <Button 
                  onClick={handleSchedule}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 text-xs font-semibold shadow-lg flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Schedule a callback
                </Button>
                <Button 
                  onClick={handleZoomMeeting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-xs font-semibold shadow-lg flex items-center justify-center gap-2"
                >
                  <Video className="w-4 h-4" />
                  Start zoom meeting
                </Button>
                <Button 
                  onClick={handleChat}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-xs font-semibold shadow-lg flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat
                </Button>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4">
              {/* Left side - Profile section */}
              <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                <div 
                  className="relative cursor-pointer mb-2" 
                  onClick={handleSarahImageClick}
                >
                  <div className="relative">
                    {/* Ripple animation behind avatar */}
                    <div className="absolute inset-0 rounded-full animate-ping bg-green-500/30 scale-110"></div>
                    <img 
                      src="/images/sara-headshot.png" 
                      alt="Live Support" 
                      width={56} height={56}
                      className="relative w-14 h-14 rounded-full border-4 border-green-500 object-cover object-top z-10 shadow-lg shadow-green-500/20" 
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white z-20"></div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-[#F4F4F4] text-sm font-semibold mb-1">Rebecca is Online</p>
                  <p className="text-[#CCCCCC] text-xs">Need help? Click to get started!</p>
                </div>
              </div>
              
              {/* Right side - 2x2 Button grid */}
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Button 
                  onClick={handleGetCallbackClick}
                  className="bg-green-600 hover:bg-green-700 text-white py-3 text-xs font-semibold shadow-lg flex items-center justify-center gap-1 px-2"
                >
                  <Phone className="w-3 h-3" />
                  <span className="block">Request a callback</span>
                </Button>
                <Button 
                  onClick={handleSchedule}
                  className="bg-orange-600 hover:bg-orange-700 text-white py-3 px-2 text-xs font-semibold shadow-lg flex items-center justify-center gap-1"
                >
                  <Calendar className="w-3 h-3" />
                  <span className="block">Schedule a callback</span>
                </Button>
                <Button 
                  onClick={handleZoomMeeting}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-2 text-xs font-semibold shadow-lg flex items-center justify-center gap-1"
                >
                  <Video className="w-3 h-3" />
                  <span className="block">Request Zoom</span>
                </Button>
                <Button 
                  onClick={handleChat}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white py-3 px-2 text-xs font-semibold shadow-lg flex items-center justify-center gap-1"
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" className="w-3 h-3 fill-current">
                    <path d="M20.52 3.48A11.8 11.8 0 0012 0C5.37 0 0 5.37 0 12c0 2.1.55 4.16 1.6 5.98L0 24l6.2-1.58A11.9 11.9 0 0012 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 22a10 10 0 01-5.1-1.39l-.36-.21-3.62.92.97-3.53-.23-.37A10 10 0 1122 12 10 10 0 0112 22zm5.3-6.3c-.29-.15-1.7-.84-1.97-.94-.26-.1-.45-.14-.63.15-.18.28-.72.94-.88 1.12-.16.18-.32.2-.6.07-.29-.15-1.2-.44-2.3-1.4-.85-.76-1.42-1.7-1.58-1.98-.16-.28-.02-.43.12-.58.12-.12.28-.32.43-.48.14-.16.19-.28.29-.47.1-.2.05-.36-.02-.51-.07-.15-.63-1.53-.86-2.1-.23-.55-.46-.48-.63-.49h-.54c-.19 0-.5.07-.76.36s-1 1-1 2.42 1.02 2.8 1.16 3 .2.44.4.66c.19.22.41.49.71.79.99.99 2.39 1.6 2.73 1.78.34.17.54.27.83.42.3.14.57.12.79.07.24-.06 1.48-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.26-.16-.55-.3z"/>
                  </svg>
                  <span className="block">WhatsApp</span>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Expanded form - callback request form only
          <div className="bg-[#364264] rounded-lg p-4 shadow-2xl border border-[#747DA1]/30 animate-slide-up w-full md:w-[400px] md:mr-auto">
            {/* Header with close button */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="relative cursor-pointer" 
                  onClick={handleSarahImageClick}
                >
                  <div className="relative">
                    {/* Ripple animation behind avatar */}
                    <div className="absolute inset-0 rounded-full animate-ping bg-green-500/30 scale-110"></div>
                    <img 
                      src="/images/sara-headshot.png" 
                      alt="Live Support" 
                      className="relative w-12 h-12 rounded-full border-4 border-green-500 object-cover object-top z-10 shadow-lg shadow-green-500/20 hover:scale-105 transition-transform" 
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white z-20"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#F4F4F4] text-sm font-semibold">Callback Request</h3>
                </div>
              </div>
              <Button
                onClick={() => setIsExpanded(false)}
                className="bg-transparent hover:bg-[#2B2F4A] text-[#F4F4F4] p-2 h-auto min-h-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-3">
              <Input 
                type="tel" 
                placeholder="Your phone (+971)" 
                value={mobile} 
                onChange={e => setMobile(e.target.value)} 
                className="border-[#747DA1] focus:border-[#D9992B] focus:ring-[#D9992B] bg-[#2B2F4A] text-[#F4F4F4] placeholder:text-[#747DA1] text-sm" 
              />
              
              <Button 
                onClick={handleCallback} 
                disabled={isSubmitting} 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-sm font-semibold flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Submit Callback Request
              </Button>
              
              <p className="text-xs text-[#747DA1] text-center">
                We'll call you back within 10 minutes
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Date Time Picker Modal */}
      <DateTimePicker 
        isOpen={showDateTimePicker}
        onClose={() => setShowDateTimePicker(false)}
        onConfirm={handleScheduleConfirm}
        phoneNumber={mobile}
        showPhoneInput={!mobile}
      />
    </>
  );
};

export default StickyMinimizedForm;
